import React, { FC, memo, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "@reach/router";
import { useDispatch, useSelector } from "react-redux";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts";
import { NavigationProps } from "../../utils/navigationProps";
import { RootState } from "../../store/rootReducer";
import { usePageTitle } from "../../hooks/usePageTitle";
import { cardQuestionText } from "../../utils/cardQuestionText";
import { cardsActions } from "../cards.slice";
import { markupFormatter, subcardKey as computeSubcardKey, CardState } from "../../common";
import { api } from "../../api/api";
import { SubcardAttempt } from "./subcardStatistics.interface";
import { useTheme } from "../../styles/ThemeContext";
import { ThemeProps, Theme } from "../../styles/theme";
import { formatInterval } from "../../utils/formatInterval";

type Props = NavigationProps & {
    cardId?: string;
    subcardKey?: string;
};

const DAY_MS = 24 * 60 * 60 * 1000;

interface SubcardMetrics {
    total: number;
    correct: number;
    accuracy: number;
    streak: number;
    lapseIntervals: number[];     // days-since-last-correct for wrong attempts
    retainedIntervals: number[];  // days-since-last-correct for correct attempts
    points: { time: number; gapDays: number; isCorrect: boolean }[];
}

function round1(value: number): number {
    return Math.round(value * 10) / 10;
}

function median(values: number[]): number | null {
    if (values.length === 0) return null;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];
}

// Treats the attempt stream as one memory: any correct answer resets the
// "last correct" clock, any wrong answer is a lapse. Used both per-subcard and,
// over the merged stream of every subcard, for the whole card.
function computeMetrics(attempts: SubcardAttempt[]): SubcardMetrics {
    const total = attempts.length;
    let correct = 0;

    let streak = 0;
    for (let i = attempts.length - 1; i >= 0; i--) {
        if (attempts[i].isCorrect) streak++;
        else break;
    }

    const lapseIntervals: number[] = [];
    const retainedIntervals: number[] = [];
    const points: { time: number; gapDays: number; isCorrect: boolean }[] = [];
    let lastCorrectTime: number | null = null;

    for (const attempt of attempts) {
        if (attempt.isCorrect) correct++;
        if (lastCorrectTime !== null) {
            const gapDays = round1((attempt.time - lastCorrectTime) / DAY_MS);
            points.push({ time: attempt.time, gapDays, isCorrect: attempt.isCorrect });
            if (attempt.isCorrect) retainedIntervals.push(gapDays);
            else lapseIntervals.push(gapDays);
        }
        if (attempt.isCorrect) lastCorrectTime = attempt.time;
    }

    return {
        total,
        correct,
        accuracy: total > 0 ? correct / total : 0,
        streak,
        lapseIntervals,
        retainedIntervals,
        points,
    };
}

function accuracyColor(accuracy: number): string {
    const hue = Math.round(accuracy * 120);
    return `hsl(${hue}, 60%, 42%)`;
}

function formatDay(time: number): string {
    const date = new Date(time);
    return `${date.getDate()}/${date.getMonth() + 1}`;
}

function formatDate(time: number): string {
    const date = new Date(time);
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
}

function plainText(markup: string): string {
    return markup.replace(/\{[^}]*\}/g, '').replace(/\*\*/g, '').replace(/\s+/g, ' ').trim();
}

const Root = styled.div<ThemeProps>`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow-y: auto;
    background-color: ${props => props.theme.primaryBackground};
    color: ${props => props.theme.primaryText};
`;

const Inner = styled.div`
    max-width: 720px;
    margin: 0 auto;
    padding: 16px;
    box-sizing: border-box;
`;

const BackLink = styled.button<ThemeProps>`
    background: none;
    border: none;
    color: ${props => props.theme.accentColor};
    font-size: 14px;
    cursor: pointer;
    padding: 4px 0;
    font-family: inherit;
`;

const Section = styled.div<ThemeProps>`
    background: ${props => props.theme.secondaryBackground};
    border: 1px solid ${props => props.theme.primaryBorder};
    border-radius: 8px;
    padding: 16px;
    margin-top: 16px;
`;

const SectionTitle = styled.div<ThemeProps>`
    font-size: 13px;
    font-weight: 600;
    color: ${props => props.theme.secondaryText};
    margin-bottom: 10px;
`;

const Caption = styled.div<ThemeProps>`
    font-size: 12px;
    color: ${props => props.theme.secondaryText};
    margin-bottom: 10px;
`;

const QuestionText = styled.div<ThemeProps>`
    font-size: 17px;
    font-weight: 600;
    color: ${props => props.theme.primaryText};
    line-height: 1.4;
`;

const AnswerText = styled.div<ThemeProps>`
    font-size: 14px;
    color: ${props => props.theme.secondaryText};
    margin-top: 6px;
    line-height: 1.4;
`;

const ScheduleLine = styled.div<ThemeProps>`
    font-size: 13px;
    color: ${props => props.theme.secondaryText};
    margin-top: 12px;
`;

const MetricsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 14px;
`;

const Metric = styled.div<ThemeProps>`
    flex: 1 1 calc(50% - 10px);
    min-width: 120px;
    background: ${props => props.theme.primaryBackground};
    border: 1px solid ${props => props.theme.primaryBorder};
    border-radius: 6px;
    padding: 10px 12px;
`;

const MetricValue = styled.div<ThemeProps>`
    font-size: 20px;
    font-weight: 700;
    color: ${props => props.theme.primaryText};
`;

const MetricLabel = styled.div<ThemeProps>`
    font-size: 12px;
    color: ${props => props.theme.secondaryText};
    margin-top: 2px;
`;

const Pips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
`;

const Pip = styled.span<{ $correct: boolean }>`
    width: 12px;
    height: 12px;
    border-radius: 3px;
    background-color: ${props => (props.$correct ? '#2e9e4f' : '#d24545')};
`;

const Empty = styled.div<ThemeProps>`
    font-size: 13px;
    color: ${props => props.theme.secondaryText};
    padding: 8px 0;
`;

const SummaryRow = styled.button<ThemeProps & { $focused: boolean }>`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    text-align: left;
    background: ${props => (props.$focused ? props.theme.hoverBackground : 'none')};
    border: none;
    border-top: 1px solid ${props => props.theme.primaryBorder};
    padding: 10px 4px;
    cursor: pointer;
    font-family: inherit;
    color: ${props => props.theme.primaryText};

    &:first-of-type {
        border-top: none;
    }
`;

const Swatch = styled.div`
    flex-shrink: 0;
    width: 46px;
    text-align: center;
    padding: 3px 6px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    color: #ffffff;
`;

const SummaryQuestion = styled.div<ThemeProps>`
    flex: 1;
    min-width: 0;
    font-size: 14px;
    color: ${props => props.theme.primaryForeground};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const MetricsView: FC<{ metrics: SubcardMetrics }> = ({ metrics }) => {
    const typicalLapse = median(metrics.lapseIntervals);
    const longestRetained = metrics.retainedIntervals.length > 0
        ? Math.max(...metrics.retainedIntervals)
        : null;

    return (
        <MetricsRow>
            <Metric>
                <MetricValue>
                    {metrics.total > 0 ? `${Math.round(metrics.accuracy * 100)}%` : '—'}
                </MetricValue>
                <MetricLabel>Accuracy ({metrics.correct}/{metrics.total})</MetricLabel>
            </Metric>
            <Metric>
                <MetricValue>{metrics.streak}</MetricValue>
                <MetricLabel>Current correct streak</MetricLabel>
            </Metric>
            <Metric>
                <MetricValue>{typicalLapse !== null ? `${round1(typicalLapse)} d` : '—'}</MetricValue>
                <MetricLabel>Typically forgotten after</MetricLabel>
            </Metric>
            <Metric>
                <MetricValue>{longestRetained !== null ? `${round1(longestRetained)} d` : '—'}</MetricValue>
                <MetricLabel>Longest retained</MetricLabel>
            </Metric>
        </MetricsRow>
    );
};

const IntervalChart: FC<{ metrics: SubcardMetrics; theme: Theme }> = ({ metrics, theme }) => {
    const data = metrics.points.map(point => ({
        label: formatDay(point.time),
        gap: point.gapDays,
        isCorrect: point.isCorrect,
    }));

    if (data.length === 0) {
        return <Empty>Not enough reviews yet — needs at least one correct answer followed by another review.</Empty>;
    }

    return (
        <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid stroke={theme.primaryBorder} strokeDasharray="3 3" />
                <XAxis
                    dataKey="label"
                    stroke={theme.secondaryText}
                    tick={{ fill: theme.secondaryText, fontSize: 11 }}
                    interval="preserveStartEnd"
                />
                <YAxis
                    stroke={theme.secondaryText}
                    tick={{ fill: theme.secondaryText, fontSize: 11 }}
                    allowDecimals={false}
                />
                <Tooltip
                    contentStyle={{
                        background: theme.secondaryBackground,
                        border: `1px solid ${theme.primaryBorder}`,
                        borderRadius: '4px',
                        color: theme.primaryText,
                    }}
                />
                <Bar dataKey="gap" name="Days since last correct" radius={[3, 3, 0, 0]}>
                    {data.map((point, index) => (
                        <Cell
                            key={index}
                            fill={point.isCorrect ? theme.successColor : theme.dangerColor}
                        />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

const SubcardStatisticsComponent: FC<Props> = (props) => {
    const { cardId } = props;
    const focusedKey = props.subcardKey;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { theme } = useTheme();

    const card = useSelector((state: RootState) =>
        cardId !== undefined ? state.cards[cardId] : undefined);

    const pageTitle = useMemo(() => {
        const q = card ? cardQuestionText(card.markup) : '';
        return `Stats: ${q || 'Card'} · Memkeep`;
    }, [card]);
    usePageTitle(pageTitle);

    useEffect(() => {
        if (cardId !== undefined && cardId.length > 0 && card === undefined) {
            dispatch(cardsActions.loadCard(cardId));
        }
    }, [cardId, card, dispatch]);

    const [detail, setDetail] = useState<Record<string, SubcardAttempt[]>>({});

    useEffect(() => {
        if (cardId === undefined || cardId.length === 0) {
            return;
        }
        let cancelled = false;
        api.statistics.getSubcardDetail(cardId)
            .then(data => {
                if (!cancelled) setDetail(data.subcards);
            })
            .catch(() => {
                if (!cancelled) setDetail({});
            });
        return () => {
            cancelled = true;
        };
    }, [cardId]);

    const subcardInfos = useMemo(() => {
        if (card === undefined) return [];
        const subcards = markupFormatter.parse(card.markup).subCards;
        return subcards.map((subcard, index) => {
            const key = computeSubcardKey(subcard.question, subcard.answer);
            const attempts = detail[key] ?? [];
            return { index, key, subcard, attempts, metrics: computeMetrics(attempts) };
        });
    }, [card, detail]);

    // Whole-card view: every subcard answer reinforces the card, so merge all
    // attempts into one chronological stream.
    const wholeCardMetrics = useMemo(() => {
        const merged: SubcardAttempt[] = [];
        for (const key of Object.keys(detail)) {
            merged.push(...detail[key]);
        }
        merged.sort((a, b) => a.time - b.time);
        return computeMetrics(merged);
    }, [detail]);

    const sortedInfos = useMemo(() => {
        return [...subcardInfos].sort((a, b) => {
            const aHas = a.metrics.total > 0 ? 1 : 0;
            const bHas = b.metrics.total > 0 ? 1 : 0;
            if (aHas !== bHas) return bHas - aHas;
            if (aHas === 1 && a.metrics.accuracy !== b.metrics.accuracy) {
                return a.metrics.accuracy - b.metrics.accuracy;
            }
            return a.index - b.index;
        });
    }, [subcardInfos]);

    const focused = subcardInfos.find(info => info.key === focusedKey);

    if (cardId === undefined || cardId.length === 0) {
        return <Root><Inner>No card.</Inner></Root>;
    }

    if (card === undefined) {
        return <Root><Inner>Loading…</Inner></Root>;
    }

    return (
        <Root>
            <Inner>
                <BackLink onClick={() => { void navigate(`/card/${encodeURIComponent(cardId)}`); }}>
                    ← Back to card
                </BackLink>

                {focused === undefined ? (
                    <Section>
                        <Empty>This subcard is not part of the current card version.</Empty>
                    </Section>
                ) : (
                    <Section>
                        <QuestionText>{plainText(focused.subcard.question)}</QuestionText>
                        <AnswerText>{plainText(focused.subcard.answer)}</AnswerText>
                        <MetricsView metrics={focused.metrics} />
                        <SectionTitle style={{ marginTop: 16 }}>Days since last correct, per review</SectionTitle>
                        <Caption>Green = remembered, red = forgot. Tall red bars mean you forgot after a long gap.</Caption>
                        <IntervalChart metrics={focused.metrics} theme={theme} />
                        {focused.metrics.total > 0 && (
                            <>
                                <SectionTitle style={{ marginTop: 16 }}>Review history</SectionTitle>
                                <Pips>
                                    {focused.attempts.map((attempt, index) => (
                                        <Pip
                                            key={index}
                                            $correct={attempt.isCorrect}
                                            title={`${formatDay(attempt.time)} — ${attempt.isCorrect ? 'correct' : 'wrong'}`}
                                        />
                                    ))}
                                </Pips>
                            </>
                        )}
                    </Section>
                )}

                <Section>
                    <SectionTitle>Whole card</SectionTitle>
                    <Caption>
                        Every subcard answer reinforces the card. Combined across all {subcardInfos.length} subcards.
                    </Caption>
                    <ScheduleLine>
                        Interval {formatInterval(card.interval)} · Next review {formatDate(card.nextCheckTime)} · {CardState[card.state]}
                    </ScheduleLine>
                    <MetricsView metrics={wholeCardMetrics} />
                    <SectionTitle style={{ marginTop: 16 }}>Days since last correct (any subcard)</SectionTitle>
                    <IntervalChart metrics={wholeCardMetrics} theme={theme} />
                </Section>

                <Section>
                    <SectionTitle>All subcards</SectionTitle>
                    <Caption>Sorted worst-first. Tap a subcard to focus it.</Caption>
                    {sortedInfos.map(info => (
                        <SummaryRow
                            key={info.key}
                            $focused={info.key === focusedKey}
                            onClick={() => { void navigate(`/card/${encodeURIComponent(cardId)}/subcard/${info.key}`); }}
                        >
                            <Swatch
                                style={{
                                    backgroundColor: info.metrics.total > 0
                                        ? accuracyColor(info.metrics.accuracy)
                                        : '#9e9e9e',
                                }}
                            >
                                {info.metrics.total > 0 ? `${Math.round(info.metrics.accuracy * 100)}%` : '—'}
                            </Swatch>
                            <SummaryQuestion>{plainText(info.subcard.question)}</SummaryQuestion>
                            <Pips>
                                {info.attempts.slice(-8).map((attempt, index) => (
                                    <Pip key={index} $correct={attempt.isCorrect} />
                                ))}
                            </Pips>
                        </SummaryRow>
                    ))}
                </Section>
            </Inner>
        </Root>
    );
};

SubcardStatisticsComponent.displayName = 'SubcardStatistics';

export const SubcardStatistics = memo(SubcardStatisticsComponent);
SubcardStatistics.displayName = 'SubcardStatistics';
